import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../../shared/lib/supabase'
import {
  extractClientTablePayload,
  extractBusinessScopingPayload,
  buildFieldProvenance
} from '../../../shared/utils/intakeFieldMapping'

/**
 * Custom hook for client intake form data management.
 *
 * Handles:
 * - Loading intake submission state (pending/submitted/unlocked)
 * - Pre-populating form from existing client data
 * - Saving form progress to clients table and assessment_responses
 * - Submitting the intake form and updating provenance tracking
 * - Unlocking the intake form (admin action)
 *
 * @param {string} clientId - The client ID
 * @param {Object} client - Client object from AssessmentContext (for pre-population)
 * @returns {Object} Intake state, form data, and action functions
 */
export function useClientIntake(clientId, client) {
  const [intakeRecord, setIntakeRecord] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  // Fetch the intake submission record
  useEffect(() => {
    if (!clientId) {
      setLoading(false)
      return
    }

    const fetchIntake = async () => {
      setLoading(true)
      try {
        const { data, error: fetchError } = await supabase
          .from('client_intake_submissions')
          .select('*')
          .eq('client_id', clientId)
          .maybeSingle()

        if (fetchError) {
          console.error('[useClientIntake] Fetch error:', fetchError)
          setError(fetchError.message)
        } else {
          setIntakeRecord(data)
        }
      } catch (err) {
        console.error('[useClientIntake] Error:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchIntake()
  }, [clientId])

  // Build initial form data from existing client record
  const buildInitialFormData = useCallback(() => {
    if (!client) return {}

    // Find the latest assessment for business scoping data
    const latestAssessment = (client.assessments || [])
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
    const businessScoping = latestAssessment?.responses?.businessScoping || {}

    return {
      // Client table fields
      name: client.name || '',
      industry: client.industry || '',
      industry_sub_category: client.industrySubCategory || '',
      staffing_size: client.staffingSize || '',
      it_team_size: client.itTeamSize || '',
      annual_revenue: client.annualRevenue || '',
      contact_name: client.contactName || '',
      contact_email: client.contactEmail || '',
      secondary_contact_name: client.secondaryContactName || '',
      secondary_contact_email: client.secondaryContactEmail || '',
      geographical_presence: client.geographicalPresence || '',
      // Business scoping fields
      security_team_size: businessScoping.security_team_size || '',
      security_function_model: businessScoping.security_function_model || '',
      operational_hq: businessScoping.operational_hq || '',
      office_location_states: businessScoping.office_location_states || [],
      data_residency_states: businessScoping.data_residency_states || [],
      international_locations: businessScoping.international_locations || '',
      business_model: businessScoping.business_model || ''
    }
  }, [client])

  // Build initial engagement context from existing business scoping data (ed-1, ed-2, ed-3)
  const buildInitialEngagementContext = useCallback(async () => {
    if (!clientId) return {}

    const latestAssessment = (client?.assessments || [])
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]

    if (!latestAssessment) return {}

    // Read from business_scoping where ed-1/ed-2/ed-3 are stored
    const bs = latestAssessment.responses?.businessScoping || {}

    // Parse ed-1 (multi-select): stored as { value: ["label1", ...], weight: 1 } or just an array
    const parseMulti = (raw) => {
      if (!raw) return { selections: [], otherText: '' }
      const val = typeof raw === 'object' && !Array.isArray(raw) ? raw.value : raw
      const arr = Array.isArray(val) ? val : (val ? [val] : [])
      // Check for "Other: ..." entries
      const otherEntry = arr.find(v => typeof v === 'string' && v.startsWith('Other: '))
      const otherText = otherEntry ? otherEntry.replace('Other: ', '') : ''
      const selections = arr.map(v => {
        if (v === otherEntry && otherEntry) return '__other__'
        return v
      })
      return { selections, otherText }
    }

    const ed1 = parseMulti(bs['ed-1'])
    const ed3 = parseMulti(bs['ed-3'])

    // Parse ed-2 (single select): stored as { value: "label", weight: 1 } or just a string
    const ed2Raw = bs['ed-2']
    const ed2Val = ed2Raw ? (typeof ed2Raw === 'object' ? ed2Raw.value : ed2Raw) : ''

    return {
      engagement_drivers: ed1.selections,
      engagement_drivers_other: ed1.otherText,
      target_timeline: ed2Val,
      success_criteria: ed3.selections,
      success_criteria_other: ed3.otherText
    }
  }, [clientId, client])

  /**
   * Save form progress (section-level save, not full submission)
   * Writes client table fields and business scoping fields to their destinations.
   */
  const saveProgress = useCallback(async (formData) => {
    if (!clientId) return false
    setSaving(true)
    setError(null)

    try {
      // 1. Update clients table (may fail silently if RLS blocks — client users need the
      //    "Client users can update assigned client" policy applied)
      const clientPayload = extractClientTablePayload(formData)
      const { error: clientError } = await supabase
        .from('clients')
        .update(clientPayload)
        .eq('id', clientId)

      if (clientError) {
        console.warn('[useClientIntake] Client table update failed:', clientError.message)
      }

      // 2. Update business scoping fields in assessment_responses (if assessment exists)
      const latestAssessment = (client?.assessments || [])
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]

      if (latestAssessment) {
        const bsPayload = extractBusinessScopingPayload(formData)

        // Merge with existing business scoping data
        const existingBs = latestAssessment.responses?.businessScoping || {}
        const mergedBs = { ...existingBs, ...bsPayload }

        const { error: bsError } = await supabase
          .from('assessment_responses')
          .upsert({
            assessment_id: latestAssessment.id,
            business_scoping: mergedBs
          }, { onConflict: 'assessment_id' })

        if (bsError) {
          console.warn('[useClientIntake] Business scoping save warning:', bsError.message)
        }
      } else {
        console.warn('[useClientIntake] No assessment found — business scoping fields not saved')
      }

      return true
    } catch (err) {
      setError(err.message)
      return false
    } finally {
      setSaving(false)
    }
  }, [clientId, client])

  /**
   * Submit the full intake form.
   * Performs final writes, updates engagement_context, and sets status to 'submitted'.
   */
  const submitIntake = useCallback(async (formData, engagementData) => {
    if (!clientId) return false
    setSubmitting(true)
    setError(null)

    try {
      // 1. Final save of all client and business scoping fields
      const clientPayload = extractClientTablePayload(formData)

      // Write selected compliance frameworks to client record
      const selectedFrameworks = formData.compliance_frameworks || []
      if (selectedFrameworks.length > 0) {
        clientPayload.regulatory_frameworks = selectedFrameworks
      }

      const { error: clientError } = await supabase
        .from('clients')
        .update(clientPayload)
        .eq('id', clientId)

      if (clientError) {
        console.warn('[useClientIntake] Client table update failed:', clientError.message)
      }

      // 2. Write business scoping + engagement data (as ed-1/ed-2/ed-3) to assessment_responses
      const latestAssessment = (client?.assessments || [])
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]

      if (latestAssessment) {
        const bsPayload = extractBusinessScopingPayload(formData)
        const existingBs = latestAssessment.responses?.businessScoping || {}
        const mergedBs = { ...existingBs, ...bsPayload }

        // Convert engagement selections to ed-1/ed-2/ed-3 format (same as admin assessment)
        const buildMultiValue = (selections, otherText) => {
          const resolved = (selections || []).map(v => {
            if (v === '__other__' && otherText) return `Other: ${otherText}`
            return v
          }).filter(v => v !== '__other__')
          return { value: resolved, weight: 1 }
        }

        if (engagementData.engagement_drivers?.length) {
          mergedBs['ed-1'] = buildMultiValue(engagementData.engagement_drivers, engagementData.engagement_drivers_other)
        }
        if (engagementData.target_timeline) {
          mergedBs['ed-2'] = { value: engagementData.target_timeline, weight: 1 }
        }
        if (engagementData.success_criteria?.length) {
          mergedBs['ed-3'] = buildMultiValue(engagementData.success_criteria, engagementData.success_criteria_other)
        }

        // Write selected frameworks as rf-1 so admin assessment displays them
        const selectedFrameworks = formData.compliance_frameworks || []
        if (selectedFrameworks.length > 0) {
          mergedBs['rf-1'] = { value: selectedFrameworks, weight: 1 }
        }

        const { error: responseError } = await supabase
          .from('assessment_responses')
          .upsert({
            assessment_id: latestAssessment.id,
            business_scoping: mergedBs
          }, { onConflict: 'assessment_id' })

        if (responseError) {
          console.warn('[useClientIntake] Assessment response save warning:', responseError.message)
        }
      }

      // 3. Build field provenance and update intake submission status
      const allFields = { ...formData, ...engagementData }
      const provenance = buildFieldProvenance(allFields)
      const now = new Date().toISOString()

      // Build framework intake fields
      const frameworksDeclared = formData.compliance_frameworks || []
      const customFrameworksRaw = formData.custom_frameworks_raw || []

      const { error: intakeError } = await supabase
        .from('client_intake_submissions')
        .upsert({
          client_id: clientId,
          status: 'submitted',
          submitted_at: now,
          field_provenance: provenance,
          frameworks_declared: frameworksDeclared,
          custom_frameworks_raw: customFrameworksRaw
        }, { onConflict: 'client_id' })

      if (intakeError) {
        throw new Error(`Intake submission failed: ${intakeError.message}`)
      }

      // Stage custom frameworks for admin review
      if (customFrameworksRaw.length > 0) {
        const stagingRows = customFrameworksRaw.map(name => ({
          client_id: clientId,
          submitted_name: name,
          submitted_by: 'client_intake'
        }))
        const { error: stagingError } = await supabase
          .from('framework_staging')
          .insert(stagingRows)
        if (stagingError) {
          console.warn('[useClientIntake] Framework staging insert failed:', stagingError.message)
        }
      }

      // Update local state
      setIntakeRecord(prev => ({
        ...prev,
        status: 'submitted',
        submitted_at: now,
        field_provenance: provenance
      }))

      return true
    } catch (err) {
      setError(err.message)
      return false
    } finally {
      setSubmitting(false)
    }
  }, [clientId, client])

  /**
   * Admin action: unlock the intake form for client edits
   */
  const unlockIntake = useCallback(async (adminUserId) => {
    if (!clientId) return false
    setError(null)

    try {
      const now = new Date().toISOString()
      const { error: unlockError } = await supabase
        .from('client_intake_submissions')
        .update({
          status: 'unlocked',
          unlocked_at: now,
          unlocked_by: adminUserId
        })
        .eq('client_id', clientId)

      if (unlockError) {
        throw new Error(`Unlock failed: ${unlockError.message}`)
      }

      setIntakeRecord(prev => ({
        ...prev,
        status: 'unlocked',
        unlocked_at: now,
        unlocked_by: adminUserId
      }))

      return true
    } catch (err) {
      setError(err.message)
      return false
    }
  }, [clientId])

  /**
   * Admin action: re-lock the intake form
   */
  const relockIntake = useCallback(async () => {
    if (!clientId) return false
    setError(null)

    try {
      const { error: lockError } = await supabase
        .from('client_intake_submissions')
        .update({ status: 'submitted' })
        .eq('client_id', clientId)

      if (lockError) {
        throw new Error(`Re-lock failed: ${lockError.message}`)
      }

      setIntakeRecord(prev => ({ ...prev, status: 'submitted' }))
      return true
    } catch (err) {
      setError(err.message)
      return false
    }
  }, [clientId])

  const status = intakeRecord?.status || 'pending'
  const isEditable = status === 'pending' || status === 'unlocked'
  const isSubmitted = status === 'submitted'

  return {
    intakeRecord,
    status,
    isEditable,
    isSubmitted,
    loading,
    saving,
    submitting,
    error,
    buildInitialFormData,
    buildInitialEngagementContext,
    saveProgress,
    submitIntake,
    unlockIntake,
    relockIntake,
    fieldProvenance: intakeRecord?.field_provenance || {}
  }
}
